import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CURRENCIES, currencySymbol } from '../currency';
import {
  getSellerMe,
  sellerLogout,
  getSellerProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getSellerOrders,
  updateOrderStatus,
  getSellerEnquiries,
  updateEnquiryStatus,
  getSellerStats,
  getCategories,
  uploadProductImage,
  compressImage,
  updateSellerProfile,
  sellerChangePassword,
  getStockForecast,
  setStockAlert,
  deleteStockAlert,
  printOrderReceipt,
  isRealEstateCategory,
  getProductImages,
  addProductImage,
  deleteProductImage,
  uploadProductMedia,
  sellerListCategories,
  sellerCreateCategory,
  sellerUpdateCategory,
  sellerDeleteCategory,
} from '../marketApi';

const UNITS = [
  'kg', 'gram', '250g', '500g',
  'litre', 'ml', '500ml',
  'piece', 'dozen', 'bundle',
  'packet', 'bag', 'box',
  'quintal', 'per apartment',
];
const STATUS_OPTIONS = ['active', 'inactive'];
const ORDER_STATUSES = ['pending', 'confirmed', 'packed', 'delivered', 'cancelled'];

const COLORS = [
  '#2563eb', '#7c3aed', '#db2777', '#d97706',
  '#16a34a', '#0891b2', '#dc2626', '#65a30d',
];

function colorFor(str) {
  if (!str) return COLORS[0];
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffff;
  return COLORS[h % COLORS.length];
}

function statusBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return 'badge-success';
  if (s === 'cancelled') return 'badge-error';
  if (s === 'confirmed') return 'badge-blue';
  if (s === 'packed') return 'badge-purple';
  return 'badge-warning';
}

function emptyProduct() {
  return {
    name: '',
    categoryId: '',
    description: '',
    price: '',
    originalPrice: '',
    currency: 'INR',
    unit: '',
    stock: '',
    imageUrl: '',
    city: '',
    status: 'active',
    courierCharge: '',
    termsAndConditions: '',
    youtubeUrl: '',
  };
}

function parseSpecs(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) return parsed.filter(s => s.key || s.value);
  } catch (_) {}
  return [];
}

function ProductModal({ product, categories, onSave, onClose }) {
  const [form, setForm] = useState(
    product
      ? {
          name: product.name || '',
          categoryId: String(product.category_id || product.categoryId || product.category?.id || ''),
          description: product.description || '',
          price: product.price || '',
          originalPrice: product.original_price || product.originalPrice || '',
          currency: product.currency || 'INR',
          unit: product.unit || 'kg',
          stock: product.stock ?? '',
          imageUrl: product.image_url || product.imageUrl || '',
          city: product.city || '',
          status: product.status || 'active',
          courierCharge: product.courier_charge != null ? String(product.courier_charge) : '',
          termsAndConditions: product.terms_and_conditions || product.termsAndConditions || '',
          youtubeUrl: product.youtube_url || product.youtubeUrl || '',
        }
      : emptyProduct()
  );
  const [specs, setSpecs] = useState(() => parseSpecs(product?.specifications));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [extraImages, setExtraImages] = useState([]); // [{ id, url, mediaType, isNew }]
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [imgUploading, setImgUploading] = useState(false);
  const multiImgRef = useRef(null);
  const videoRef = useRef(null);
  const pdfRef = useRef(null);

  const selectedCat = categories.find(c => String(c.id) === String(form.categoryId));
  const isRealEstate = isRealEstateCategory(selectedCat?.name || '');

  useEffect(() => {
    if (product && product.id) {
      getProductImages(product.id)
        .then(imgs => setExtraImages(imgs.map(i => ({ id: i.id, url: i.image_url, mediaType: i.media_type || 'image', isNew: false }))))
        .catch(() => {});
    }
  }, [product?.id]);

  async function handleMultiImageUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImgUploading(true);
    try {
      for (const file of files) {
        const compressed = await compressImage(file);
        const res = await uploadProductMedia(compressed);
        setExtraImages(prev => [...prev, { id: null, url: res.url, mediaType: 'image', isNew: true }]);
      }
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setImgUploading(false);
      if (multiImgRef.current) multiImgRef.current.value = '';
    }
  }

  async function handleVideoUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImgUploading(true);
    try {
      for (const file of files) {
        const res = await uploadProductMedia(file);
        setExtraImages(prev => [...prev, { id: null, url: res.url, mediaType: 'video', isNew: true }]);
      }
    } catch (err) {
      setUploadError(err.message || 'Video upload failed');
    } finally {
      setImgUploading(false);
      if (videoRef.current) videoRef.current.value = '';
    }
  }

  async function handlePdfUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setImgUploading(true);
    try {
      for (const file of files) {
        const res = await uploadProductMedia(file);
        setExtraImages(prev => [...prev, { id: null, url: res.url, mediaType: 'pdf', name: file.name, isNew: true }]);
      }
    } catch (err) {
      setUploadError(err.message || 'PDF upload failed');
    } finally {
      setImgUploading(false);
      if (pdfRef.current) pdfRef.current.value = '';
    }
  }

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleImageUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const res = await uploadProductImage(compressed);
      setForm((prev) => ({ ...prev, imageUrl: res.url }));
    } catch (err) {
      setUploadError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Product name is required'); return; }
    if (!form.price || isNaN(form.price) || Number(form.price) <= 0) {
      setError('Please enter a valid price');
      return;
    }
    if (!isRealEstate && (form.stock === '' || isNaN(form.stock) || Number(form.stock) < 0)) {
      setError('Please enter a valid stock quantity');
      return;
    }
    setError('');
    setLoading(true);
    const validSpecs = specs.filter(s => s.key.trim() || s.value.trim());
    const payload = {
      name: form.name.trim(),
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      description: form.description.trim(),
      price: Number(form.price),
      originalPrice: form.originalPrice ? Number(form.originalPrice) : null,
      currency: form.currency || 'INR',
      unit: form.unit,
      stock: form.stock !== '' ? Number(form.stock) : 0,
      imageUrl: form.imageUrl.trim(),
      city: form.city.trim() || null,
      status: form.status,
      specifications: validSpecs.length > 0 ? JSON.stringify(validSpecs) : null,
      courierCharge: form.courierCharge !== '' ? Number(form.courierCharge) : null,
      termsAndConditions: form.termsAndConditions.trim() || null,
      youtubeUrl: form.youtubeUrl.trim() || null,
    };
    try {
      await onSave(payload, { extraImages, pendingDeleteIds });
    } catch (e) {
      setError(e.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{product ? 'Edit Product' : 'Add New Product'}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Product Name *</label>
              <input className="form-control" name="name" value={form.name} onChange={handleChange} placeholder="e.g. Fresh Tomatoes" />
            </div>
            <div className="two-col">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-control" name="categoryId" value={form.categoryId} onChange={handleChange}>
                  <option value="">Select category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Unit</label>
                <select className="form-control" name="unit" value={form.unit} onChange={handleChange}>
                  <option value="">-- Select unit --</option>
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea className="form-control" name="description" value={form.description} onChange={handleChange} placeholder="Brief product description" rows={2} />
            </div>
            <div className="form-group">
              <label className="form-label">Currency</label>
              <select className="form-control" name="currency" value={form.currency} onChange={handleChange}>
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</option>
                ))}
              </select>
            </div>
            <div className="two-col">
              <div className="form-group">
                <label className="form-label">{isRealEstate ? `Starting Price (${currencySymbol(form.currency)}) *` : `Price (${currencySymbol(form.currency)}) *`}</label>
                <input className="form-control" name="price" type="number" min="0" step="0.01" value={form.price} onChange={handleChange} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">{isRealEstate ? `Max Price (${currencySymbol(form.currency)})` : `Original Price (${currencySymbol(form.currency)})`}</label>
                <input className="form-control" name="originalPrice" type="number" min="0" step="0.01" value={form.originalPrice} onChange={handleChange} placeholder={isRealEstate ? 'Upper range' : 'For strikethrough'} />
              </div>
            </div>
            <div className="two-col">
              <div className="form-group">
                <label className="form-label">Stock{!isRealEstate && ' *'}</label>
                <input className="form-control" name="stock" type="number" min="0" value={form.stock} onChange={handleChange} placeholder={isRealEstate ? 'Optional' : 'Available qty'} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-control" name="status" value={form.status} onChange={handleChange}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">
                Courier Charge ({currencySymbol(form.currency)}){' '}
                <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>(optional — leave blank for free delivery)</span>
              </label>
              <input className="form-control" name="courierCharge" type="number" min="0" step="0.50"
                value={form.courierCharge} onChange={handleChange} placeholder="0.00 = Free delivery" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Listing Cities <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>(comma-separated — overrides your default city — use ALL to show everywhere)</span>
              </label>
              <input className="form-control" name="city" value={form.city} onChange={handleChange} placeholder="e.g. Hyderabad, Bangalore — or ALL — leave blank to use your registered city" />
            </div>
            <div className="form-group">
              <label className="form-label">
                Product Image <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>(optional)</span>
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  className="form-control"
                  name="imageUrl"
                  value={form.imageUrl}
                  onChange={handleChange}
                  placeholder="Paste image URL or upload below"
                  style={{ flex: 1 }}
                />
                {/* gallery picker */}
                <input
                  type="file"
                  accept="image/*"
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                {/* camera — capture="environment" opens rear camera directly on mobile */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={cameraInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImageUpload}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => cameraInputRef.current && cameraInputRef.current.click()}
                  disabled={uploading}
                  title="Take photo with camera"
                >
                  📷
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => imageInputRef.current && imageInputRef.current.click()}
                  disabled={uploading}
                  title="Upload from gallery or files"
                >
                  {uploading ? 'Processing…' : '⬆ Upload'}
                </button>
              </div>
              {uploadError && (
                <div style={{ fontSize: 12, color: 'var(--error)', marginTop: 4 }}>{uploadError}</div>
              )}
              {form.imageUrl && (
                <img
                  src={form.imageUrl}
                  alt="preview"
                  style={{ marginTop: 8, maxHeight: 100, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--border)' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                  onLoad={(e) => { e.target.style.display = 'block'; }}
                />
              )}
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                Upload a photo (≤ 5 MB) or paste a direct image URL. Left blank shows the product initial letter.
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 8 }}>
                <label className="form-label">
                  Photos, Videos &amp; Documents
                  <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}> (upload multiple)</span>
                </label>
                {/* Media grid */}
                {extraImages.filter(img => !pendingDeleteIds.includes(img.id)).length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8, marginBottom: 10 }}>
                    {extraImages.filter(img => !pendingDeleteIds.includes(img.id)).map((img, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        {img.mediaType === 'video' ? (
                          <video
                            src={img.url}
                            style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)', background: '#000' }}
                            muted
                            playsInline
                          />
                        ) : img.mediaType === 'pdf' ? (
                          <div style={{ width: '100%', aspectRatio: '1/1', background: '#fef9ee', border: '1px solid var(--border)', borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 4 }}>
                            <span style={{ fontSize: 28 }}>📄</span>
                            <span style={{ fontSize: 9, color: 'var(--muted)', wordBreak: 'break-all', textAlign: 'center', marginTop: 2, maxWidth: '100%', overflow: 'hidden' }}>
                              {(img.name || img.url.split('/').pop() || 'PDF').slice(0, 20)}
                            </span>
                          </div>
                        ) : (
                          <img src={img.url} alt="" style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)' }} />
                        )}
                        {img.mediaType === 'video' && (
                          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <span style={{ color: '#fff', fontSize: 12, marginLeft: 2 }}>▶</span>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (img.id) {
                              setPendingDeleteIds(prev => [...prev, img.id]);
                            } else {
                              setExtraImages(prev => prev.filter((_, idx) => idx !== i));
                            }
                          }}
                          style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Upload inputs */}
                <input type="file" accept="image/*" multiple ref={multiImgRef} style={{ display: 'none' }} onChange={handleMultiImageUpload} />
                <input type="file" accept="video/*" multiple ref={videoRef} style={{ display: 'none' }} onChange={handleVideoUpload} />
                <input type="file" accept="application/pdf" multiple ref={pdfRef} style={{ display: 'none' }} onChange={handlePdfUpload} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => multiImgRef.current && multiImgRef.current.click()} disabled={imgUploading}>
                    {imgUploading ? 'Uploading…' : '📷 Add Photos'}
                  </button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => videoRef.current && videoRef.current.click()} disabled={imgUploading}>
                    🎥 Add Video
                  </button>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => pdfRef.current && pdfRef.current.click()} disabled={imgUploading}>
                    📄 Add PDF
                  </button>
                  {extraImages.filter(img => !pendingDeleteIds.includes(img.id)).length > 0 && (
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {extraImages.filter(img => !pendingDeleteIds.includes(img.id) && img.mediaType === 'image').length} photo(s),{' '}
                      {extraImages.filter(img => !pendingDeleteIds.includes(img.id) && img.mediaType === 'video').length} video(s),{' '}
                      {extraImages.filter(img => !pendingDeleteIds.includes(img.id) && img.mediaType === 'pdf').length} PDF(s)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Photos ≤ 5 MB · Videos ≤ 200 MB · PDFs ≤ 50 MB</div>
              </div>
            {/* Specifications */}
            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label">
                Specifications
                <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}> (optional — e.g. Color: Red, Weight: 500g)</span>
              </label>
              {specs.map((spec, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    className="form-control"
                    placeholder="Label (e.g. Color)"
                    value={spec.key}
                    onChange={e => setSpecs(prev => prev.map((s, idx) => idx === i ? { ...s, key: e.target.value } : s))}
                    style={{ flex: 1 }}
                  />
                  <input
                    className="form-control"
                    placeholder="Value (e.g. Red)"
                    value={spec.value}
                    onChange={e => setSpecs(prev => prev.map((s, idx) => idx === i ? { ...s, value: e.target.value } : s))}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => setSpecs(prev => prev.filter((_, idx) => idx !== i))}
                    style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0 10px', cursor: 'pointer', color: 'var(--muted)', fontSize: 16, flexShrink: 0 }}
                    title="Remove"
                  >×</button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setSpecs(prev => [...prev, { key: '', value: '' }])}
                style={{ marginTop: 2 }}
              >+ Add Specification</button>
            </div>

            {/* YouTube Video Link */}
            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label">
                YouTube Video Link
                <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}> (optional — product demo, how-to, etc.)</span>
              </label>
              <input
                className="form-control"
                name="youtubeUrl"
                value={form.youtubeUrl}
                onChange={handleChange}
                placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
              />
              {form.youtubeUrl && !/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/.test(form.youtubeUrl) && (
                <div style={{ fontSize: 11, color: 'var(--error)', marginTop: 4 }}>Invalid YouTube URL</div>
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label">
                Terms &amp; Conditions
                <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}> (optional — return policy, warranty, usage conditions, etc.)</span>
              </label>
              <textarea
                className="form-control"
                name="termsAndConditions"
                value={form.termsAndConditions}
                onChange={handleChange}
                placeholder="e.g. No returns after 7 days. Warranty void if tampered. Cash on delivery only."
                rows={3}
              />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button type="button" className="btn btn-ghost btn-full" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                {loading ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const STATUS_META = {
  delivered:  { label: 'Delivered',  color: 'var(--success)',  bg: '#f0fdf4' },
  confirmed:  { label: 'Confirmed',  color: '#2563eb',         bg: '#eff6ff' },
  packed:     { label: 'Packed',     color: '#7c3aed',         bg: '#f5f3ff' },
  pending:    { label: 'Pending',    color: '#d97706',         bg: '#fffbeb' },
  cancelled:  { label: 'Cancelled',  color: 'var(--error)',    bg: '#fef2f2' },
};

function fmtPeriodLabel(period, view) {
  if (!period) return '';
  if (view === 'day') {
    const d = new Date(period + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }
  if (view === 'week') {
    const d = new Date(period + 'T00:00:00');
    return 'Wk ' + d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }
  if (view === 'month') {
    const [y, m] = period.split('-');
    return new Date(+y, +m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
  }
  return period; // year
}

function BarChart({ data, view, metric }) {
  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '32px 0', fontSize: 14 }}>
        No data for this period
      </div>
    );
  }

  const isItems  = metric === 'items';
  const getValue = d => isItems ? parseInt(d.items_sold || 0) : parseFloat(d.revenue || 0);
  const maxVal   = Math.max(...data.map(getValue), 1);
  const BAR_H    = 120;
  const barColor = isItems
    ? 'linear-gradient(180deg, #7c3aed 0%, #a78bfa 100%)'
    : 'linear-gradient(180deg, var(--primary) 0%, #60a5fa 100%)';

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, minWidth: data.length * 48, padding: '8px 4px 0' }}>
        {data.map((d, i) => {
          const val    = getValue(d);
          const rev    = parseFloat(d.revenue || 0);
          const items  = parseInt(d.items_sold || 0);
          const orders = parseInt(d.orders || 0);
          const h      = Math.max(4, Math.round((val / maxVal) * BAR_H));
          const topLabel = isItems
            ? val
            : (rev >= 1000 ? '₹' + (rev / 1000).toFixed(1) + 'k' : '₹' + rev.toFixed(0));
          const tooltip = `₹${rev.toFixed(2)} · ${items} items · ${orders} order${orders !== 1 ? 's' : ''}`;
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 0 40px' }}>
              <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 3 }}>{topLabel}</div>
              <div
                title={tooltip}
                style={{
                  width: '100%', height: h, borderRadius: '4px 4px 0 0',
                  background: barColor, cursor: 'default', transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
              />
              <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 4, textAlign: 'center', lineHeight: 1.2, wordBreak: 'break-all' }}>
                {fmtPeriodLabel(String(d.period), view)}
              </div>
              <div style={{ fontSize: 9, fontWeight: 600, color: isItems ? '#7c3aed' : 'var(--primary)' }}>
                {isItems ? (items > 0 ? `₹${rev.toFixed(0)}` : '') : (orders > 0 ? `${items} items` : '')}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: 'var(--muted)', textAlign: 'right', marginTop: 6 }}>
        {isItems ? 'bar = items sold · below = revenue' : 'bar = revenue · below = items sold'}
      </div>
    </div>
  );
}

function fmtDuration(minutes) {
  if (!minutes || minutes <= 0) return null;
  const m = Math.round(Number(minutes));
  if (m < 60) return `${m} min`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

const PRODUCT_PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week',  label: '7 Days' },
  { key: 'month', label: 'Month' },
  { key: 'year',  label: 'Year' },
  { key: 'total', label: 'All Time' },
];

function StatsTab() {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [view, setView]     = useState('month');
  const [metric, setMetric] = useState('revenue'); // 'revenue' | 'items'
  const [productPeriod, setProductPeriod] = useState('month');
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    getSellerStats()
      .then(setStats)
      .catch(e => setError(e.message || 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div>
        {[80, 80, 160, 200].map((h, i) => (
          <div key={i} className="skeleton" style={{ height: h, marginBottom: 12, borderRadius: 12 }} />
        ))}
      </div>
    );
  }
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!stats) return null;

  const byStatus        = stats.byStatus || [];
  const byProductPeriod = stats.byProductPeriod || [];
  const chartData       = { day: stats.byDay, week: stats.byWeek, month: stats.byMonth, year: stats.byYear }[view] || [];
  const avgDelivery     = stats.avgDelivery || {};
  const avgDeliveryStr  = fmtDuration(avgDelivery.avgMinutes);

  const productRows = byProductPeriod.filter(r => {
    if (!productSearch.trim()) return true;
    const q = productSearch.toLowerCase();
    return (
      String(r.product_id || '').includes(q) ||
      (r.product_name || '').toLowerCase().includes(q)
    );
  });

  const totalOrders  = byStatus.reduce((s, r) => s + parseInt(r.order_count || 0), 0);
  const totalRevenue = byStatus.reduce((s, r) => s + parseFloat(r.revenue || 0), 0);
  const totalItems   = byStatus.reduce((s, r) => s + parseInt(r.items_sold || 0), 0);
  const delivered    = byStatus.find(r => r.status === 'delivered');
  const activeCount  = byStatus
    .filter(r => ['pending', 'confirmed', 'packed'].includes(r.status))
    .reduce((s, r) => s + parseInt(r.order_count || 0), 0);

  // Today's data from byDay (last entry if it matches today)
  const todayStr  = new Date().toISOString().slice(0, 10);
  const todayData = (stats.byDay || []).find(d => String(d.period) === todayStr);
  const todayRev  = parseFloat(todayData?.revenue || 0);
  const todayItems = parseInt(todayData?.items_sold || 0);

  const summaryCards = [
    { label: 'Total Orders',    value: totalOrders,                    sub: 'all time',              color: '#2563eb', bg: '#eff6ff', icon: '🛒' },
    { label: 'Total Revenue',   value: '₹' + totalRevenue.toFixed(2), sub: 'all time',              color: '#16a34a', bg: '#f0fdf4', icon: '💰' },
    { label: 'Items Sold',      value: totalItems,                     sub: 'all time',              color: '#7c3aed', bg: '#f5f3ff', icon: '📦' },
    { label: "Today's Revenue", value: '₹' + todayRev.toFixed(2),     sub: todayItems + ' items',   color: '#d97706', bg: '#fffbeb', icon: '📅' },
    { label: 'Delivered',       value: '₹' + parseFloat(delivered?.revenue || 0).toFixed(2), sub: parseInt(delivered?.order_count || 0) + ' orders', color: '#16a34a', bg: '#dcfce7', icon: '✅' },
    { label: 'Active Orders',   value: activeCount,                    sub: 'pending/confirmed/packed', color: '#ef4444', bg: '#fef2f2', icon: '⏳' },
    { label: 'Avg Delivery',   value: avgDeliveryStr || '—',          sub: `${avgDelivery.deliveredCount || 0} delivered`, color: '#0891b2', bg: '#ecfeff', icon: '🕐' },
  ];

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
        {summaryCards.map(c => (
          <div key={c.label} className="card" style={{ padding: '14px 12px', background: c.bg, border: `1px solid ${c.color}22` }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{c.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: c.color, marginTop: 2 }}>{c.label}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Status breakdown */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Orders by Status
        </div>
        {byStatus.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 14 }}>No orders yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {byStatus.map(row => {
              const meta    = STATUS_META[row.status] || { label: row.status, color: 'var(--muted)', bg: 'var(--bg)' };
              const count   = parseInt(row.order_count || 0);
              const revenue = parseFloat(row.revenue || 0);
              const items   = parseInt(row.items_sold || 0);
              const pct     = totalOrders > 0 ? Math.round((count / totalOrders) * 100) : 0;
              return (
                <div key={row.status}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                        fontSize: 12, fontWeight: 700,
                        color: meta.color, background: meta.bg, border: `1px solid ${meta.color}44`,
                      }}>
                        {meta.label}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{count} order{count !== 1 ? 's' : ''}</span>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{items} items</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: meta.color }}>₹{revenue.toFixed(2)}</div>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', background: meta.color, borderRadius: 3, transition: 'width 0.4s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="card" style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {metric === 'items' ? 'Items Sold Over Time' : 'Revenue Over Time'}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {/* Metric toggle */}
            <div style={{ display: 'flex', gap: 4, marginRight: 8 }}>
              <button onClick={() => setMetric('revenue')} className={metric === 'revenue' ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'} style={{ padding: '4px 10px', fontSize: 12 }}>
                ₹ Revenue
              </button>
              <button onClick={() => setMetric('items')} className={metric === 'items' ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'} style={{ padding: '4px 10px', fontSize: 12, background: metric === 'items' ? '#7c3aed' : undefined, borderColor: metric === 'items' ? '#7c3aed' : undefined }}>
                # Items
              </button>
            </div>
            {/* Period toggle */}
            {[['day','Day'],['week','Week'],['month','Month'],['year','Year']].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)}
                className={view === v ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <BarChart data={chartData} view={view} metric={metric} />
      </div>

      {/* Sales by Product */}
      <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Sales by Product
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {PRODUCT_PERIODS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setProductPeriod(key)}
                className={productPeriod === key ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'}
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <input
          className="form-control"
          placeholder="Search by product name or ID…"
          value={productSearch}
          onChange={e => setProductSearch(e.target.value)}
          style={{ marginBottom: 12, fontSize: 13 }}
        />

        {byProductPeriod.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 14, textAlign: 'center', padding: '16px 0' }}>
            No product sales data yet
          </div>
        ) : productRows.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 14, textAlign: 'center', padding: '16px 0' }}>
            No products match "{productSearch}"
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>ID</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Product</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Items</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {productRows.map((r, i) => {
                  const items   = parseInt(r[`items_${productPeriod}`] || 0);
                  const revenue = parseFloat(r[`revenue_${productPeriod}`] || 0);
                  const maxRev  = Math.max(...productRows.map(x => parseFloat(x[`revenue_${productPeriod}`] || 0)), 1);
                  const barPct  = Math.round((revenue / maxRev) * 100);
                  return (
                    <tr key={r.product_id || i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 8px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>#{r.product_id}</td>
                      <td style={{ padding: '8px 8px' }}>
                        <div style={{ fontWeight: 600 }}>{r.product_name || '—'}</div>
                        {barPct > 0 && (
                          <div style={{ marginTop: 4, height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: barPct + '%', background: 'var(--primary)', borderRadius: 2, transition: 'width 0.4s' }} />
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>
                        {items > 0 ? items : <span style={{ color: 'var(--muted)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: revenue > 0 ? 'var(--primary)' : 'var(--muted)' }}>
                        {revenue > 0 ? '₹' + revenue.toFixed(2) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg)' }}>
                  <td colSpan={2} style={{ padding: '8px 8px', fontWeight: 700, fontSize: 12 }}>Total ({productRows.length} products)</td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 800, color: '#7c3aed' }}>
                    {productRows.reduce((s, r) => s + parseInt(r[`items_${productPeriod}`] || 0), 0)}
                  </td>
                  <td style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 800, color: 'var(--primary)' }}>
                    ₹{productRows.reduce((s, r) => s + parseFloat(r[`revenue_${productPeriod}`] || 0), 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ProductsTab() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    // Load seller's own categories + platform categories they use
    sellerListCategories()
      .then(rows => setCategories(Array.isArray(rows) ? rows : []))
      .catch(() => getCategories().then(setCategories).catch(() => {}));
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // null | 'add' | product object
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getSellerProducts()
      .then((data) => {
        setProducts(Array.isArray(data) ? data : (data.content || []));
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || 'Failed to load products');
        setLoading(false);
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave(payload, images = {}) {
    let productId;
    if (modal && modal.id) {
      await updateProduct(modal.id, payload);
      productId = modal.id;
    } else {
      const result = await createProduct(payload);
      productId = result.id;
    }
    // Save new extra images/videos
    for (const img of (images.extraImages || [])) {
      if (img.isNew && img.url) {
        await addProductImage(productId, img.url, 0, img.mediaType || 'image');
      }
    }
    // Delete removed images
    for (const imgId of (images.pendingDeleteIds || [])) {
      if (imgId) await deleteProductImage(productId, imgId);
    }
    setModal(null);
    load();
  }

  async function handleDelete(id) {
    setDeleting(true);
    try {
      await deleteProduct(id);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) {
    return (
      <div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
        ))}
      </div>
    );
  }

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: 'var(--muted)', fontSize: 14 }}>{products.length} product{products.length !== 1 ? 's' : ''}</span>
        <button className="btn btn-primary" onClick={() => setModal('add')}>
          + Add Product
        </button>
      </div>

      {products.length === 0 ? (
        <div>
          <div
            style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)',
              border: '1px solid #c7d2fe',
              borderRadius: 16,
              padding: '28px 24px',
              marginBottom: 20,
            }}
          >
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 16, color: '#1e40af' }}>
              How to add your products
            </div>
            {[
              { icon: '1', text: 'Click "+ Add Product" button above' },
              { icon: '2', text: 'Enter product name, category and price per unit' },
              { icon: '3', text: 'Set available stock quantity' },
              { icon: '4', text: 'Add an image URL (optional) to show product photo' },
              { icon: '5', text: 'Click Save — product goes live instantly for buyers in your city' },
            ].map((s) => (
              <div key={s.icon} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
                <span
                  style={{
                    background: '#2563eb',
                    color: '#fff',
                    borderRadius: '50%',
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {s.icon}
                </span>
                <span style={{ fontSize: 14, paddingTop: 2 }}>{s.text}</span>
              </div>
            ))}
            <div
              style={{
                marginTop: 16,
                padding: '10px 14px',
                background: '#fef3c7',
                borderRadius: 8,
                fontSize: 13,
                color: '#92400e',
              }}
            >
              Tip: Products appear for buyers in your registered city. Use "Listing Cities" on each product to target specific cities (comma-separated), or enter ALL to show the product in every city.
            </div>
          </div>
          <div className="empty-state">
            <div className="empty-state-icon">📦</div>
            <h3>No products yet</h3>
            <p style={{ marginTop: 6 }}>Click "+ Add Product" to list your first item</p>
          </div>
        </div>
      ) : (
        <div className="card table-wrap">
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.name} className="prod-thumb" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        <div
                          className="prod-thumb-placeholder"
                          style={{ background: colorFor(p.name) }}
                        >
                          {(p.name || 'P').charAt(0)}
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                        {p.unit && <div style={{ fontSize: 11, color: 'var(--muted)' }}>per {p.unit}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--muted)', fontSize: 13 }}>
                    {p.category_name || p.categoryName || p.category?.name || '—'}
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    {currencySymbol(p.currency)}{p.price}
                    {p.courier_charge > 0 && (
                      <div style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>
                        +{currencySymbol(p.currency)}{p.courier_charge} courier
                      </div>
                    )}
                    {(p.courier_charge === 0 || p.courier_charge === null || p.courier_charge === undefined) && (
                      <div style={{ fontSize: 10, color: 'var(--success)', fontWeight: 400 }}>Free delivery</div>
                    )}
                  </td>
                  <td>
                    <span
                      style={{
                        color: p.stock === 0 ? 'var(--error)' : p.stock <= 10 ? 'var(--warning)' : 'var(--success)',
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      {p.stock}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${p.status === 'active' ? 'badge-success' : 'badge-muted'}`}
                    >
                      {p.status || 'active'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => setModal(p)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(p)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <ProductModal
          product={modal === 'add' ? null : modal}
          categories={categories}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Delete Product</span>
              <button className="modal-close" onClick={() => setDeleteConfirm(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: 20 }}>
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-ghost btn-full" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger btn-full"
                  onClick={() => handleDelete(deleteConfirm.id)}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ENQUIRY_STATUSES = ['new', 'contacted', 'closed'];

function enquiryBadgeClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'closed') return 'badge-success';
  if (s === 'contacted') return 'badge-blue';
  return 'badge-warning';
}

function OrdersTab({ sellerId, businessName }) {
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});
  const [updatingEnq, setUpdatingEnq] = useState({});

  useEffect(() => {
    Promise.all([
      getSellerOrders(),
      getSellerEnquiries().catch(() => []),
    ]).then(([ordersData, enquiriesData]) => {
      setOrders(Array.isArray(ordersData) ? ordersData : (ordersData.content || []));
      setEnquiries(Array.isArray(enquiriesData) ? enquiriesData : (enquiriesData.content || []));
      setLoading(false);
    }).catch((e) => {
      setError(e.message || 'Failed to load orders');
      setLoading(false);
    });
  }, []);

  async function handleStatusChange(orderId, status) {
    setUpdating((prev) => ({ ...prev, [orderId]: true }));
    try {
      await updateOrderStatus(orderId, status);
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status } : o))
      );
    } catch (e) {
      setError(e.message || 'Failed to update order status');
    } finally {
      setUpdating((prev) => ({ ...prev, [orderId]: false }));
    }
  }

  async function handleEnquiryStatusChange(enquiryId, status) {
    setUpdatingEnq((prev) => ({ ...prev, [enquiryId]: true }));
    try {
      await updateEnquiryStatus(enquiryId, status);
      setEnquiries((prev) =>
        prev.map((e) => (e.id === enquiryId ? { ...e, status } : e))
      );
    } catch (e) {
      setError(e.message || 'Failed to update enquiry status');
    } finally {
      setUpdatingEnq((prev) => ({ ...prev, [enquiryId]: false }));
    }
  }

  if (loading) {
    return (
      <div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 80, marginBottom: 10, borderRadius: 12 }} />
        ))}
      </div>
    );
  }

  if (error) return <div className="alert alert-error">{error}</div>;

  if (orders.length === 0 && enquiries.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">🛒</div>
        <h3>No orders or enquiries yet</h3>
        <p style={{ marginTop: 6 }}>Orders and service enquiries from buyers will appear here</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {orders.map((order) => {
        const orderRef      = order.order_ref    || order.orderRef    || `#${order.id}`;
        const buyerName     = order.buyer_name   || order.buyerName   || '';
        const buyerPhone    = order.buyer_phone  || order.phone       || '';
        const buyerAddress  = order.buyer_address || order.buyerAddress || '';
        const createdAt     = order.created_at   || order.createdAt   || null;
        const deliveredAt   = order.delivered_at || order.deliveredAt  || null;
        const deliveryMins  = (createdAt && deliveredAt)
          ? (new Date(deliveredAt) - new Date(createdAt)) / 60000
          : null;
        const deliveryStr   = fmtDuration(deliveryMins);
        const itemsTotal = (order.items || []).reduce(
          (s, i) => s + (i.price || 0) * (i.quantity || 1), 0
        );

        return (
          <div key={order.id} className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{orderRef}</div>
                {createdAt && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                    {new Date(createdAt).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </div>
                )}
              </div>
              <span className={`badge ${statusBadgeClass(order.status)}`} style={{ padding: '4px 10px' }}>
                {(order.status || 'pending').toUpperCase()}
              </span>
            </div>

            {/* Buyer */}
            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 4 }}>
              {buyerName && <strong style={{ color: 'var(--text)' }}>{buyerName}</strong>}
              {buyerPhone && <span>{' · '}{buyerPhone}</span>}
              {order.city && <span>{' · '}{order.city}</span>}
            </div>
            {buyerAddress && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                📍 {buyerAddress}
              </div>
            )}
            {deliveryStr && (
              <div style={{ fontSize: 12, color: '#0891b2', marginBottom: 4, fontWeight: 600 }}>
                🕐 Delivered in {deliveryStr}
              </div>
            )}

            {/* Items — only this seller's products */}
            {order.items && order.items.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--primary)',
                  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
                }}>
                  Your products to deliver
                </div>
                {order.items.map((item, i) => {
                  const name = item.product_name || item.productName || item.name
                    || `Product #${item.product_id || item.productId}`;
                  const subtotal = (item.price || 0) * (item.quantity || 1);
                  return (
                    <div key={i} style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span>{name} × {item.quantity}</span>
                      <span style={{ fontWeight: 600 }}>₹{subtotal.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>
                Your total: ₹{itemsTotal.toFixed(2)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="btn btn-outline btn-sm"
                  style={{ fontSize: 12, padding: '5px 10px' }}
                  onClick={() => printOrderReceipt(order, orderRef, businessName)}
                  title="Print order receipt"
                >
                  🖨️ Print
                </button>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Update status:</span>
                <select
                  className="form-control"
                  style={{ width: 'auto', padding: '5px 10px', fontSize: 13 }}
                  value={order.status || 'pending'}
                  onChange={(e) => handleStatusChange(order.id, e.target.value)}
                  disabled={updating[order.id]}
                >
                  {ORDER_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {updating[order.id] && (
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>Saving...</span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Enquiries section ── */}
      {enquiries.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{
            fontWeight: 700, fontSize: 15, marginBottom: 14,
            paddingBottom: 10, borderBottom: '2px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            📩 Service / Real Estate Enquiries
            <span className="badge badge-blue" style={{ fontSize: 12 }}>{enquiries.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {enquiries.map((enq) => {
              const createdAt = enq.created_at || enq.createdAt || null;
              return (
                <div key={enq.id} className="card" style={{ padding: 16, borderLeft: '3px solid #6366f1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {enq.product_name || enq.productName || `Product #${enq.product_id || enq.productId}`}
                      </div>
                      {createdAt && (
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                          {new Date(createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                    <span className={`badge ${enquiryBadgeClass(enq.status)}`} style={{ padding: '4px 10px', fontSize: 12 }}>
                      {(enq.status || 'new').toUpperCase()}
                    </span>
                  </div>

                  <div style={{ fontSize: 13, marginBottom: 6 }}>
                    <strong>{enq.name || enq.buyerName}</strong>
                    {(enq.phone || enq.buyerPhone) && <span style={{ color: 'var(--muted)' }}>{' · '}{enq.phone || enq.buyerPhone}</span>}
                    {enq.email && <span style={{ color: 'var(--muted)' }}>{' · '}{enq.email}</span>}
                  </div>

                  {(enq.message || enq.requirement) && (
                    <div style={{
                      fontSize: 13, color: 'var(--text)',
                      background: 'var(--bg)', borderRadius: 6,
                      padding: '8px 10px', marginBottom: 10,
                      fontStyle: 'italic',
                    }}>
                      "{enq.message || enq.requirement}"
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>Status:</span>
                    <select
                      className="form-control"
                      style={{ width: 'auto', padding: '5px 10px', fontSize: 13 }}
                      value={enq.status || 'new'}
                      onChange={(e) => handleEnquiryStatusChange(enq.id, e.target.value)}
                      disabled={updatingEnq[enq.id]}
                    >
                      {ENQUIRY_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {updatingEnq[enq.id] && (
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Saving...</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileTab({ seller, sellerStatus, onLogout, logoutLoading, onProfileUpdate }) {
  // ── Profile edit state ─────────────────────────────────────────────
  const [editing, setEditing]         = useState(false);
  const [form, setForm]               = useState({
    businessName:      seller.business_name      || seller.businessName      || '',
    ownerName:         seller.owner_name         || seller.ownerName         || '',
    whatsapp:          seller.whatsapp           || seller.phone             || '',
    email:             seller.email              || '',
    city:              seller.city               || '',
    customDomain:      seller.custom_domain      || '',
    logoUrl:           seller.logo_url           || '',
    description:       seller.description        || '',
    termsAndConditions: seller.terms_and_conditions || '',
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadErr, setLogoUploadErr] = useState('');
  const logoInputRef = useRef(null);
  const [profSaving, setProfSaving] = useState(false);
  const [profErr, setProfErr]       = useState('');
  const [profOk, setProfOk]         = useState(false);

  async function handleLogoUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLogoUploadErr('');
    setLogoUploading(true);
    try {
      const compressed = await compressImage(file);
      const res = await uploadProductImage(compressed);
      setForm(prev => ({ ...prev, logoUrl: res.url }));
    } catch (err) {
      setLogoUploadErr(err.message || 'Upload failed');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }

  function startEdit() {
    setForm({
      businessName:      seller.business_name      || seller.businessName      || '',
      ownerName:         seller.owner_name         || seller.ownerName         || '',
      whatsapp:          seller.whatsapp           || seller.phone             || '',
      email:             seller.email              || '',
      city:              seller.city               || '',
      customDomain:      seller.custom_domain      || '',
      logoUrl:           seller.logo_url           || '',
      description:       seller.description        || '',
      termsAndConditions: seller.terms_and_conditions || '',
    });
    setProfErr(''); setProfOk(false);
    setEditing(true);
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setProfErr(''); setProfOk(false);
    if (!form.businessName.trim()) { setProfErr('Business name is required'); return; }
    if (!form.ownerName.trim())    { setProfErr('Owner name is required');    return; }
    setProfSaving(true);
    try {
      const res = await updateSellerProfile(form);
      setProfOk(true);
      setEditing(false);
      if (onProfileUpdate && res.seller) onProfileUpdate(res.seller);
    } catch (err) {
      setProfErr(err.message || 'Failed to save profile');
    } finally {
      setProfSaving(false);
    }
  }

  // ── Password change state ──────────────────────────────────────────
  const [pwd, setPwd]           = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdErr, setPwdErr]     = useState('');
  const [pwdOk, setPwdOk]       = useState(false);

  async function handleChangePwd(e) {
    e.preventDefault();
    setPwdErr(''); setPwdOk(false);
    if (!pwd.currentPassword) { setPwdErr('Enter your current password'); return; }
    if (pwd.newPassword.length < 6) { setPwdErr('New password must be at least 6 characters'); return; }
    if (pwd.newPassword !== pwd.confirmPassword) { setPwdErr('Passwords do not match'); return; }
    setPwdSaving(true);
    try {
      await sellerChangePassword({ currentPassword: pwd.currentPassword, newPassword: pwd.newPassword });
      setPwdOk(true);
      setPwd({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwdErr(err.message || 'Failed to change password');
    } finally {
      setPwdSaving(false);
    }
  }

  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div style={{ maxWidth: 520 }}>

      {/* ── Profile card ── */}
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>🏪 Business Profile</div>
          {!editing && (
            <button className="btn btn-sm btn-secondary" onClick={startEdit}>Edit Profile</button>
          )}
        </div>

        {profOk && !editing && (
          <div className="alert alert-success" style={{ marginBottom: 12 }}>Profile updated successfully!</div>
        )}

        {editing ? (
          <form onSubmit={handleSaveProfile}>
            {profErr && <div className="alert alert-error" style={{ marginBottom: 12 }}>{profErr}</div>}

            <div className="form-group">
              <label className="form-label">Business Name *</label>
              <input className="form-control" value={form.businessName} onChange={f('businessName')}
                placeholder="Your shop / business name" required />
            </div>
            <div className="form-group">
              <label className="form-label">Owner Name *</label>
              <input className="form-control" value={form.ownerName} onChange={f('ownerName')}
                placeholder="Your full name" required />
            </div>
            <div className="form-group">
              <label className="form-label">WhatsApp Number</label>
              <input className="form-control" value={form.whatsapp} onChange={f('whatsapp')}
                placeholder="WhatsApp contact number" />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={form.email} onChange={f('email')}
                placeholder="Business email address" />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-control" value={form.city} onChange={f('city')}
                placeholder="City where you operate" />
            </div>
            <div className="form-group">
              <label className="form-label">Custom Domain / Subdomain</label>
              <input className="form-control" value={form.customDomain} onChange={f('customDomain')}
                placeholder="e.g. shop.yourdomain.com or sub.smabizz.com" />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                Point your domain/subdomain DNS to this server, then enter it here. Visitors to your domain will see only your store.
                Leave blank to use the default marketplace at <strong>marketplace.smabizz.com</strong>.
              </div>
            </div>

            {/* Business Logo */}
            <div className="form-group">
              <label className="form-label">
                Business Logo <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>(optional)</span>
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="form-control" value={form.logoUrl} onChange={f('logoUrl')}
                  placeholder="Paste logo URL or upload" style={{ flex: 1 }} />
                <input type="file" accept="image/*" ref={logoInputRef} style={{ display: 'none' }} onChange={handleLogoUpload} />
                <button type="button" className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={() => logoInputRef.current && logoInputRef.current.click()} disabled={logoUploading}>
                  {logoUploading ? 'Uploading…' : '⬆ Upload'}
                </button>
              </div>
              {logoUploadErr && <div style={{ fontSize: 12, color: 'var(--error)', marginTop: 4 }}>{logoUploadErr}</div>}
              {form.logoUrl && (
                <img src={form.logoUrl} alt="logo preview"
                  style={{ marginTop: 8, maxHeight: 64, maxWidth: 200, borderRadius: 6, objectFit: 'contain', border: '1px solid var(--border)', background: '#fff', padding: 4 }}
                  onError={e => e.target.style.display = 'none'}
                  onLoad={e => e.target.style.display = 'block'} />
              )}
            </div>

            {/* Business Description */}
            <div className="form-group">
              <label className="form-label">
                Business Description <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>(optional)</span>
              </label>
              <textarea className="form-control" rows={3} value={form.description} onChange={f('description')}
                placeholder="Tell customers about your business, what you sell, your speciality..." />
            </div>

            {/* Business Terms & Conditions */}
            <div className="form-group">
              <label className="form-label">
                Business Terms &amp; Conditions <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 11 }}>(optional)</span>
              </label>
              <textarea className="form-control" rows={3} value={form.termsAndConditions} onChange={f('termsAndConditions')}
                placeholder="Return policy, delivery terms, payment conditions, warranty..." />
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button type="submit" className="btn btn-primary" disabled={profSaving}>
                {profSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => { setEditing(false); setProfErr(''); }}
                disabled={profSaving}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            {(seller.logo_url) && (
              <div style={{ marginBottom: 16 }}>
                <img src={seller.logo_url} alt="Business logo"
                  style={{ maxHeight: 72, maxWidth: 220, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border)', background: '#fff', padding: 6 }} />
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14 }}>
              {[
                { label: 'Business Name',   value: seller.business_name || seller.businessName },
                { label: 'Owner Name',      value: seller.owner_name    || seller.ownerName    },
                { label: 'Mobile',          value: seller.phone                                },
                { label: 'WhatsApp',        value: seller.whatsapp      || seller.phone        },
                { label: 'Email',           value: seller.email         || '—'                 },
                { label: 'City',            value: seller.city          || '—'                 },
                { label: 'Custom Domain',   value: seller.custom_domain || '(marketplace.smabizz.com)' },
                { label: 'Status',          value: sellerStatus         || '—'                 },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', gap: 12 }}>
                  <span style={{ color: 'var(--muted)', minWidth: 110 }}>{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            <hr className="divider" />
            <button className="btn btn-danger" onClick={onLogout} disabled={logoutLoading} style={{ marginTop: 8 }}>
              {logoutLoading ? 'Logging out...' : 'Logout'}
            </button>
            <div style={{ marginTop: 12 }}>
              <Link to="/" style={{ fontSize: 13, color: 'var(--muted)' }}>← Back to Marketplace</Link>
            </div>
          </>
        )}
      </div>

      {/* ── Change password card ── */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>🔒 Change Password</div>
        {pwdErr && <div className="alert alert-error"   style={{ marginBottom: 12 }}>{pwdErr}</div>}
        {pwdOk  && <div className="alert alert-success" style={{ marginBottom: 12 }}>Password changed successfully!</div>}
        <form onSubmit={handleChangePwd}>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input className="form-control" type="password" placeholder="Current password"
              value={pwd.currentPassword}
              onChange={e => { setPwd(p => ({ ...p, currentPassword: e.target.value })); setPwdOk(false); setPwdErr(''); }} />
          </div>
          <div className="form-group">
            <label className="form-label">New Password <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 12 }}>(min 6 characters)</span></label>
            <input className="form-control" type="password" placeholder="New password"
              value={pwd.newPassword}
              onChange={e => { setPwd(p => ({ ...p, newPassword: e.target.value })); setPwdOk(false); setPwdErr(''); }} />
          </div>
          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input className="form-control" type="password" placeholder="Repeat new password"
              value={pwd.confirmPassword}
              onChange={e => { setPwd(p => ({ ...p, confirmPassword: e.target.value })); setPwdOk(false); setPwdErr(''); }} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={pwdSaving} style={{ marginTop: 4 }}>
            {pwdSaving ? 'Saving…' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

const PERIODS = [
  { key: 'req_day',       label: 'Day',        days: 1   },
  { key: 'req_week',      label: 'Week',       days: 7   },
  { key: 'req_month',     label: 'Month',      days: 30  },
  { key: 'req_quarter',   label: 'Quarter',    days: 90  },
  { key: 'req_half_year', label: '6 Months',   days: 180 },
  { key: 'req_year',      label: 'Year',       days: 365 },
];

function stockStatus(current, required) {
  if (required === 0) return 'none';
  if (current === 0)  return 'out';
  if (current < required) return 'low';
  return 'ok';
}

const CAT_ICONS = ['📦','🛒','🥦','🍎','🥛','🐟','🍿','🥤','📱','👕','🏠','📚','🌾','🍚','🧴','💊','🪴','🧁','🍕','🔧','🧰','💎','🪨','🏗️','🌿'];

function SellerCategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null); // null | 'new' | {cat}
  const [deleting, setDeleting] = useState({});
  const [form, setForm] = useState({ name: '', icon: '📦', sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    sellerListCategories()
      .then(data => { setCats(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(e => { setErr(e.message || 'Failed to load'); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() { setForm({ name: '', icon: '📦', sortOrder: 0 }); setFormErr(''); setModal('new'); }
  function openEdit(c) { setForm({ name: c.name, icon: c.icon || '📦', sortOrder: c.sort_order || 0 }); setFormErr(''); setModal(c); }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) { setFormErr('Name is required'); return; }
    setSaving(true); setFormErr('');
    try {
      if (modal === 'new') await sellerCreateCategory({ name: form.name.trim(), icon: form.icon, sortOrder: Number(form.sortOrder) });
      else                 await sellerUpdateCategory(modal.id, { name: form.name.trim(), icon: form.icon, sortOrder: Number(form.sortOrder) });
      setModal(null); load();
    } catch (e) { setFormErr(e.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  async function handleDelete(cat) {
    if (!window.confirm(`Delete "${cat.name}"?`)) return;
    setDeleting(d => ({ ...d, [cat.id]: true }));
    try { await sellerDeleteCategory(cat.id); load(); }
    catch (e) { setErr(e.message || 'Delete failed'); }
    finally { setDeleting(d => ({ ...d, [cat.id]: false })); }
  }

  const myCats      = cats.filter(c => c.is_own);
  const platformCats = cats.filter(c => !c.is_own);

  return (
    <div>
      {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          Manage categories for your store. Custom categories appear alongside platform categories on your storefront.
        </div>
        <button className="btn btn-primary" onClick={openNew}>➕ New Category</button>
      </div>

      {loading ? (
        Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton" style={{ height: 44, marginBottom: 8, borderRadius: 8 }} />
        ))
      ) : (
        <>
          {/* My custom categories */}
          <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 8 }}>
            My Categories ({myCats.length})
          </div>
          {myCats.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 16, padding: '12px', background: 'var(--surface)', borderRadius: 8 }}>
              No custom categories yet. Add one to organise your products.
            </div>
          ) : (
            <div className="table-wrap" style={{ marginBottom: 20 }}>
              <table className="data-table">
                <thead><tr><th>Icon</th><th>Name</th><th style={{ textAlign: 'right' }}>Products</th><th>Actions</th></tr></thead>
                <tbody>
                  {myCats.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontSize: 22, textAlign: 'center', width: 50 }}>{c.icon}</td>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td style={{ textAlign: 'right', color: c.product_count > 0 ? 'var(--primary)' : 'var(--muted)' }}>{c.product_count}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>✏️ Edit</button>
                          <button className="btn btn-sm" disabled={deleting[c.id]}
                            style={{ background: '#fce8e6', color: '#c5221f', border: 'none' }}
                            onClick={() => handleDelete(c)}>
                            {deleting[c.id] ? '…' : '🗑️'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Platform categories in use */}
          {platformCats.length > 0 && (
            <>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 8 }}>
                Platform Categories in Use ({platformCats.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {platformCats.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'var(--surface)', borderRadius: 20, fontSize: 13 }}>
                    <span style={{ fontSize: 18 }}>{c.icon}</span>
                    <span>{c.name}</span>
                    <span style={{ color: 'var(--muted)', fontSize: 11 }}>({c.product_count})</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* Add / Edit modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{modal === 'new' ? '➕ New Category' : '✏️ Edit Category'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="modal-body">
              {formErr && <div className="alert alert-error" style={{ marginBottom: 12 }}>{formErr}</div>}
              <form onSubmit={handleSave}>
                <div className="form-group">
                  <label className="form-label">Category Name *</label>
                  <input className="form-control" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Granite Slabs, Black Galaxy…" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Icon</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 32 }}>{form.icon}</span>
                    <input className="form-control" value={form.icon} maxLength={8}
                      onChange={e => setForm(f => ({ ...f, icon: e.target.value }))} style={{ width: 80 }} />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {CAT_ICONS.map(ic => (
                      <button type="button" key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
                        style={{ fontSize: 20, background: form.icon === ic ? '#e8f0fe' : '#f8f9fa',
                          border: form.icon === ic ? '2px solid #1a73e8' : '1px solid #e8eaed',
                          borderRadius: 8, padding: '4px 6px', cursor: 'pointer' }}>
                        {ic}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <input className="form-control" type="number" min={0} value={form.sortOrder}
                    onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))} style={{ width: 100 }} />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                  <button type="button" className="btn btn-ghost btn-full" onClick={() => setModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary btn-full" disabled={saving}>
                    {saving ? 'Saving…' : modal === 'new' ? 'Create' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StockForecastTab() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState('');
  const [saving, setSaving] = useState({});
  const [thresholds, setThresholds] = useState({});   // productId → input value
  const [saved, setSaved]   = useState({});            // productId → bool flash

  const load = useCallback(() => {
    setLoading(true);
    getStockForecast()
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setRows(list);
        const init = {};
        list.forEach(r => { init[r.id] = r.alert_threshold > 0 ? String(r.alert_threshold) : ''; });
        setThresholds(init);
        setLoading(false);
      })
      .catch(e => { setErr(e.message || 'Failed to load forecast'); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSaveAlert(productId) {
    setSaving(s => ({ ...s, [productId]: true }));
    const val = parseInt(thresholds[productId], 10);
    try {
      if (!val || val <= 0) {
        await deleteStockAlert(productId);
        setRows(prev => prev.map(r => r.id === productId ? { ...r, alert_threshold: 0 } : r));
      } else {
        await setStockAlert(productId, val);
        setRows(prev => prev.map(r => r.id === productId ? { ...r, alert_threshold: val } : r));
      }
      setSaved(s => ({ ...s, [productId]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [productId]: false })), 1800);
    } catch (e) {
      setErr(e.message || 'Failed to save alert');
    } finally {
      setSaving(s => ({ ...s, [productId]: false }));
    }
  }

  const alertCount = rows.filter(r => r.alert_threshold > 0 && r.stock < r.alert_threshold).length;
  const outCount   = rows.filter(r => r.stock === 0).length;

  if (loading) return (
    <div>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height: 52, marginBottom: 8, borderRadius: 8 }} />
      ))}
    </div>
  );

  return (
    <div>
      {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}

      {/* Summary badges */}
      {(alertCount > 0 || outCount > 0) && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {outCount > 0 && (
            <div style={{ background: '#fce8e6', border: '1px solid #f28b82', borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>🚫</span>
              <div>
                <div style={{ fontWeight: 700, color: '#c5221f', fontSize: 13 }}>{outCount} product{outCount > 1 ? 's' : ''} out of stock</div>
                <div style={{ fontSize: 11, color: '#888' }}>Restock immediately</div>
              </div>
            </div>
          )}
          {alertCount > 0 && (
            <div style={{ background: '#fef3cd', border: '1px solid #fbbc04', borderRadius: 8, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, color: '#b45309', fontSize: 13 }}>{alertCount} product{alertCount > 1 ? 's' : ''} below alert threshold</div>
                <div style={{ fontSize: 11, color: '#888' }}>Stock has fallen below your set limit</div>
              </div>
            </div>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <h3>No products yet</h3>
          <p>Add products to see stock forecasts.</p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            Stock requirements are calculated from your average daily sales rate. Set an alert threshold — you'll see a warning when stock drops below it.
          </div>
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 160 }}>Product</th>
                  <th style={{ textAlign: 'right', minWidth: 80 }}>Current Stock</th>
                  <th style={{ textAlign: 'right', minWidth: 80 }}>Daily Rate</th>
                  {PERIODS.map(p => (
                    <th key={p.key} style={{ textAlign: 'right', minWidth: 70 }}>
                      {p.label}<div style={{ fontSize: 9, fontWeight: 400, color: '#aaa' }}>min qty</div>
                    </th>
                  ))}
                  <th style={{ minWidth: 160 }}>Alert Threshold</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const belowAlert = row.alert_threshold > 0 && row.stock < row.alert_threshold;
                  return (
                    <tr key={row.id} style={{ background: row.stock === 0 ? '#fff5f5' : belowAlert ? '#fffbea' : undefined }}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {row.stock === 0 && <span title="Out of stock" style={{ marginRight: 4 }}>🚫</span>}
                          {belowAlert && row.stock > 0 && <span title="Below alert" style={{ marginRight: 4 }}>⚠️</span>}
                          {row.name}
                        </div>
                        <div style={{ fontSize: 11, color: '#888' }}>{row.unit}</div>
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700,
                        color: row.stock === 0 ? '#c5221f' : belowAlert ? '#b45309' : '#137333' }}>
                        {row.stock}
                      </td>
                      <td style={{ textAlign: 'right', color: '#555', fontSize: 12 }}>
                        {row.daily_rate > 0 ? row.daily_rate.toFixed(2) : <span style={{ color: '#bbb' }}>—</span>}
                      </td>
                      {PERIODS.map(p => {
                        const req = row[p.key];
                        const st  = stockStatus(row.stock, req);
                        const color = st === 'out' ? '#c5221f' : st === 'low' ? '#b45309' : st === 'ok' ? '#137333' : '#bbb';
                        const bg    = st === 'out' ? '#fce8e6' : st === 'low' ? '#fef3cd' : 'transparent';
                        return (
                          <td key={p.key} style={{ textAlign: 'right' }}>
                            <span style={{ background: bg, color, borderRadius: 6, padding: '2px 6px', fontWeight: req > 0 ? 600 : 400, fontSize: 12 }}>
                              {req > 0 ? req : <span style={{ color: '#ccc' }}>—</span>}
                            </span>
                          </td>
                        );
                      })}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="number"
                            min={0}
                            placeholder="e.g. 10"
                            value={thresholds[row.id] ?? ''}
                            onChange={e => setThresholds(t => ({ ...t, [row.id]: e.target.value }))}
                            style={{ width: 72, padding: '4px 8px', borderRadius: 6, border: '1px solid #e0e0e0', fontSize: 12 }}
                          />
                          <button
                            className="btn btn-sm btn-primary"
                            disabled={saving[row.id]}
                            onClick={() => handleSaveAlert(row.id)}
                            style={{ padding: '4px 10px', fontSize: 12, flexShrink: 0 }}
                          >
                            {saved[row.id] ? '✓' : saving[row.id] ? '…' : '🔔 Set'}
                          </button>
                          {row.alert_threshold > 0 && (
                            <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                              alert @ {row.alert_threshold}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 10 }}>
            🟢 sufficient &nbsp;🟡 below requirement &nbsp;🔴 out of stock &nbsp;— no sales data yet
          </div>
        </>
      )}
    </div>
  );
}

export default function SellerDashboard() {
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    getSellerMe()
      .then((data) => {
        setSeller(data);
        setLoading(false);
      })
      .catch(() => {
        navigate('/seller/login');
      });
  }, [navigate]);

  async function handleLogout() {
    setLogoutLoading(true);
    try {
      await sellerLogout();
    } catch {
      // ignore
    }
    navigate('/seller/login');
  }

  if (loading) {
    return (
      <div className="page-container" style={{ textAlign: 'center', paddingTop: 60 }}>
        <div className="skeleton" style={{ height: 32, width: 200, margin: '0 auto 16px' }} />
        <div className="skeleton" style={{ height: 16, width: 300, margin: '0 auto' }} />
      </div>
    );
  }

  const sellerStatus = seller?.status || '';
  const isPending = sellerStatus === 'pending';
  const isRejected = sellerStatus === 'rejected';

  return (
    <div className="page-container">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ fontWeight: 800, fontSize: 22 }}>{seller?.business_name || seller?.businessName || 'Seller Dashboard'}</h2>
          <div style={{ color: 'var(--muted)', fontSize: 14, marginTop: 2 }}>
            {seller?.city && <span>{seller.city} · </span>}
            {seller?.owner_name || seller?.ownerName}
            {sellerStatus && (
              <span
                className={`badge ${
                  sellerStatus === 'approved' ? 'badge-success' :
                  sellerStatus === 'rejected' ? 'badge-error' : 'badge-warning'
                }`}
                style={{ marginLeft: 8 }}
              >
                {sellerStatus}
              </span>
            )}
          </div>
        </div>
        <button
          className="btn btn-ghost"
          onClick={handleLogout}
          disabled={logoutLoading}
        >
          {logoutLoading ? 'Logging out...' : 'Logout'}
        </button>
      </div>

      {/* Status alerts */}
      {isPending && (
        <div className="alert alert-warning">
          Your account is pending admin approval. You can browse the dashboard but cannot make changes until approved.
        </div>
      )}
      {isRejected && (
        <div className="alert alert-error">
          Your seller account has been rejected. Please contact support for more information.
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab${activeTab === 'dashboard' ? ' active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={`tab${activeTab === 'products' ? ' active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          📦 My Products
        </button>
        <button
          className={`tab${activeTab === 'orders' ? ' active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          🛒 Orders & Enquiries
        </button>
        <button
          className={`tab${activeTab === 'stock' ? ' active' : ''}`}
          onClick={() => setActiveTab('stock')}
        >
          📈 Stock Forecast
        </button>
        <button
          className={`tab${activeTab === 'categories' ? ' active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          🏷️ My Categories
        </button>
        <button
          className={`tab${activeTab === 'profile' ? ' active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Profile
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <StatsTab />
      )}

      {activeTab === 'products' && (
        <ProductsTab />
      )}

      {activeTab === 'orders' && (
        <OrdersTab sellerId={seller?.id} businessName={seller?.business_name || seller?.businessName || 'SmaBizZ Marketplace'} />
      )}

      {activeTab === 'stock' && (
        <StockForecastTab />
      )}

      {activeTab === 'categories' && (
        <SellerCategoriesTab />
      )}

      {activeTab === 'profile' && seller && (
        <ProfileTab seller={seller} sellerStatus={sellerStatus} onLogout={handleLogout} logoutLoading={logoutLoading}
          onProfileUpdate={updated => setSeller(s => ({ ...s, ...updated }))} />
      )}
    </div>
  );
}
